import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Download, SlidersHorizontal, X } from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { Badge } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { formatDate } from '../../../utils/Utils';
import {
  BANDS, GOAL_BANDS, Q, downloadBlob, exportEmployees, fetchEmployees, formatNum, formatScore, listParams, orgComparisonLink, profileLink,
} from '../config';

// PM analitik P2 — E03 alt alan: çalışan sonuç tablosu (kimlik, dönem organizasyonu, rol, bileşen puanları, final puan, bant,
// yayımlanma ve değişim). Liste = EmployeesPage aynası (DataTable, sunucu tarafı sayfalama, başlık yalnız tablo başlığında).
// Pano sayfasında olduğu için tablo kendi içinde KAYMAZ (tek kaydırma: sayfa). Sonuç süzgeçleri (bant, kalibrasyon değişti,
// yayımlandı, veri eksik) URL'dedir; grafik tıklaması aynı anahtarları yazar → liste satır sayısı grafik sayısıyla aynı kuraldan.
// Final yoksa puan hücreleri boş kalır (sıfır yazılmaz). Satır → E14 çalışan profili.
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const ChipRemove = styled(Button)`
  min-height: 0;
  padding: 0;
  line-height: 0;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const Wrap = styled.div`
  grid-column: span 12;
  min-width: 0;
`;

const SORT_OF_COLUMN = { fullName: 'name', finalValue: 'finalValue', band: 'band' };
const LIST_KEYS = ['band', 'bucket', 'goalBand', 'calibrationChanged', 'published', 'missing'];
const BOOL_OPTIONS = ['true', 'false'];

export default function EmployeeList({
  filters, page, setPage, prelim = false, isHr = false, comparePeriod, distribution,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [drawerInitial, setDrawerInitial] = useState(null);
  const [exporting, setExporting] = useState(false);
  const list = useMemo(() => listParams(page), [page]);
  const resetKey = JSON.stringify([filters, list, prelim, comparePeriod?.id]);
  const [table, setTable] = useState({ resetKey, pageNumber: 1, pageSize: 10, sort: null, order: 'asc' });
  // Süzgeç değişince ilk sayfaya dönülür (eski sayfa numarası yeni kümede boş sayfa açmasın).
  const effective = table.resetKey === resetKey ? table : { ...table, resetKey, pageNumber: 1 };
  const tableKey = JSON.stringify(effective);
  const panel = usePanel(() => fetchEmployees(filters, {
    pageNumber: effective.pageNumber, pageSize: effective.pageSize, list, sort: effective.sort, order: effective.order,
    comparePeriodId: comparePeriod?.id, prelim,
  }), [tableKey], !!filters.periodId);

  const rows = panel.data?.data || [];
  const total = panel.data?.totalRowCount || 0;

  const bucketName = (key) => {
    const b = (distribution?.levels || []).find((x) => x.key === key);
    if (!b) return key;
    if (b.label) return b.label;
    return b.to === null || b.to === undefined ? `${formatNum(b.from, lang, 0)}+` : `${formatNum(b.from, lang, 0)}–${formatNum(b.to, lang, 0)}`;
  };

  const chipText = (k) => {
    const v = page[k];
    switch (k) {
      case 'band': return `${t('performance.results.analytics.list.filter.band')}: ${t(`performance.results.analytics.band.${v}`)}`;
      case 'goalBand': return `${t('performance.results.analytics.list.filter.goalBand')}: ${t(`performance.results.analytics.goalBand.${v}`)}`;
      case 'bucket': return `${t('performance.results.analytics.list.filter.bucket')}: ${bucketName(v)}`;
      default: return `${t(`performance.results.analytics.list.filter.${k}`)}: ${t(`performance.results.analytics.list.bool.${v}`)}`;
    }
  };
  const active = LIST_KEYS.filter((k) => page[k]);

  const boolOptions = BOOL_OPTIONS.map((v) => ({ id: v, name: t(`performance.results.analytics.list.bool.${v}`) }));
  const schema = [
    ...(prelim ? [] : [{
      name: 'band', label: t('performance.results.analytics.list.filter.band'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name',
      options: BANDS.map((b) => ({ id: b, name: t(`performance.results.analytics.band.${b}`) })),
    }]),
    {
      name: 'goalBand', label: t('performance.results.analytics.list.filter.goalBand'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name',
      options: GOAL_BANDS.map((b) => ({ id: b, name: t(`performance.results.analytics.goalBand.${b}`) })),
    },
    ...(distribution?.scaleMode && !prelim ? [{
      name: 'calibrationChanged', label: t('performance.results.analytics.list.filter.calibrationChanged'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name', options: boolOptions,
    }] : []),
    { name: 'published', label: t('performance.results.analytics.list.filter.published'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name', options: boolOptions },
    { name: 'missing', label: t('performance.results.analytics.list.filter.missing'), type: 'select', grid: 12, idKey: 'id', labelKey: 'name', options: boolOptions },
  ];

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportEmployees(filters, {
        list, sort: effective.sort, order: effective.order, comparePeriodId: comparePeriod?.id, prelim,
      });
      downloadBlob(res, 'performans-sonuclari.xlsx');
    } finally {
      setExporting(false);
    }
  };

  const onTableChanged = (action, state) => {
    const sortName = state.sortOrder?.name;
    setTable({
      resetKey,
      pageNumber: (state.page || 0) + 1,
      pageSize: state.rowsPerPage || 10,
      sort: sortName ? SORT_OF_COLUMN[sortName] || null : null,
      order: state.sortOrder?.direction || 'asc',
    });
  };

  const rowOf = (meta) => rows[meta.rowIndex] || {};
  const dash = '-';
  const scoreCell = (v) => (v === null || v === undefined ? dash : formatNum(v, lang));
  const columns = [
    {
      name: 'fullName',
      label: t('performance.results.analytics.list.col.employee'),
      options: {
        sort: true, filter: false,
        customBodyRender: (v, meta) => (
          <Link to={profileLink(rowOf(meta).employeeId, filters.periodId)} onClick={(e) => e.stopPropagation()} data-link="profile">{v}</Link>
        ),
      },
    },
    {
      name: 'departmentName',
      label: t('performance.results.analytics.list.col.department'),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          // Organizasyon hücresi → E10 (birim karşılaştırması yalnız İK).
          if (!v) return dash;
          return isHr && r.departmentId
            ? <Link to={orgComparisonLink(filters, { [Q.groupBy]: 'department', [Q.focus]: r.departmentId })} onClick={(e) => e.stopPropagation()} data-link="org">{v}</Link>
            : v;
        },
      },
    },
    { name: 'titleName', label: t('performance.results.analytics.list.col.title'), options: { sort: false, filter: false, customBodyRender: (v) => v || dash } },
    { name: 'managerName', label: t('performance.results.analytics.list.col.manager'), options: { sort: false, filter: false, customBodyRender: (v) => v || dash } },
    { name: 'goalScore', label: t('performance.results.analytics.list.col.goalScore'), options: { sort: false, filter: false, customBodyRender: scoreCell } },
    { name: 'competencyScore', label: t('performance.results.analytics.list.col.competencyScore'), options: { sort: false, filter: false, customBodyRender: scoreCell } },
    { name: 'combinedScore', label: t('performance.results.analytics.list.col.combinedScore'), options: { sort: false, filter: false, customBodyRender: scoreCell } },
    {
      name: 'finalValue',
      label: prelim ? t('performance.results.analytics.list.col.preliminary') : t('performance.results.analytics.list.col.final'),
      options: {
        sort: true, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          if (r.isFinal) {
            return (
              <span data-final={r.participantId}>
                {r.finalLevelLabel ? `${r.finalLevelLabel} · ` : ''}
                {formatScore(r.finalValue, r.scaleMax, lang)}
              </span>
            );
          }
          if (r.preliminaryValue !== null && r.preliminaryValue !== undefined) {
            return (
              <span data-preliminary={r.participantId}>
                <Badge $tone="info">{t('performance.results.analytics.list.preliminaryBadge')}</Badge>
                {' '}
                {r.preliminaryLevelLabel ? `${r.preliminaryLevelLabel} · ` : ''}
                {formatNum(r.preliminaryValue, lang)}
              </span>
            );
          }
          return <Muted data-noresult={r.participantId}>{t(`performance.results.analytics.list.state.${r.resultState || 'none'}`)}</Muted>;
        },
      },
    },
    {
      name: 'band',
      label: t('performance.results.analytics.list.col.band'),
      options: {
        sort: !prelim, filter: false,
        customBodyRender: (v) => (v ? <Badge $tone={v === 'low' ? 'danger' : v === 'high' ? 'success' : 'info'}>{t(`performance.results.analytics.band.${v}`)}</Badge> : dash),
      },
    },
    {
      name: 'calibrationChanged',
      label: t('performance.results.analytics.list.col.calibration'),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          if (v !== true) return dash;
          return (
            <span>
              <Badge>{t('performance.results.analytics.list.calibrationChanged')}</Badge>
              {r.proposedLevelLabel ? <Muted>{` ${t('performance.results.analytics.list.proposed', { level: r.proposedLevelLabel })}`}</Muted> : null}
            </span>
          );
        },
      },
    },
    {
      name: 'published',
      label: t('performance.results.analytics.list.col.published'),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          if (!v) return <Muted>{t('performance.results.analytics.list.notPublished')}</Muted>;
          return (
            <span>
              {t('performance.results.analytics.list.publishedOn', { date: r.publishedAt ? formatDate(r.publishedAt, lang) : dash })}
              {r.acknowledgedAt ? <Muted>{` · ${t('performance.results.analytics.list.acknowledged')}`}</Muted> : null}
            </span>
          );
        },
      },
    },
    {
      name: 'change',
      label: t('performance.results.analytics.list.col.change'),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          if (v !== null && v !== undefined) return `${v > 0 ? '+' : ''}${formatNum(v, lang)}`;
          if (r.changeUnavailableReason) return <Muted>{t(`performance.results.analytics.list.changeReason.${r.changeUnavailableReason}`)}</Muted>;
          return dash;
        },
      },
    },
  ];

  return (
    <Wrap data-panel="employees">
      {active.length > 0 ? (
        <Chips>
          {active.map((k) => (
            <Chip key={k} data-chip={k}>
              {chipText(k)}
              <ChipRemove variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={() => setPage({ [Q[k]]: null })} data-action="remove-chip">
                <X size={12} />
              </ChipRemove>
            </Chip>
          ))}
        </Chips>
      ) : null}
      {panel.error ? (
        <>
          <LoadErrorBanner message={t('performance.operations.panel.error')} />
          <Button variant="outline" onClick={panel.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
        </>
      ) : null}
      <DataTable
        title={prelim ? t('performance.results.analytics.list.titlePreliminary') : t('performance.results.analytics.list.title')}
        fetching={panel.loading}
        columns={columns}
        data={rows}
        totalRowCount={total}
        pageIndex={effective.pageNumber - 1}
        pageSize={effective.pageSize}
        onTableChanged={onTableChanged}
        onRowClick={(row) => history.push(profileLink(row.employeeId, filters.periodId))}
        customIdSelector="participantId"
        disableFilters
        tableBodyMaxHeight="none"
        toolbarActions={(
          <>
            <Button variant="outline" onClick={() => setDrawerInitial(Object.fromEntries(LIST_KEYS.map((k) => [k, page[k] || ''])))} data-action="list-filters">
              <SlidersHorizontal size={14} />
              {' '}
              {t('performance.results.analytics.list.filters')}
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={exporting || total === 0} data-action="export">
              <Download size={14} />
              {' '}
              {exporting ? t('common.loading') : t('performance.results.analytics.list.export')}
            </Button>
          </>
        )}
      />
      <GenericFormDrawer
        open={drawerInitial !== null}
        onClose={() => setDrawerInitial(null)}
        title={t('performance.results.analytics.list.filters')}
        schema={schema}
        initialValues={drawerInitial || undefined}
        submitLabel={t('performance.operations.filter.apply')}
        onSave={async (values) => {
          setPage(Object.fromEntries(LIST_KEYS.filter((k) => k !== 'bucket').map((k) => [Q[k], values[k] || null])));
          return true;
        }}
      />
    </Wrap>
  );
}
